// ════════════════════════════════════════════════════════════════════════
//  The editing session on one draft
//
//  Everything that happens to a draft lives here: load it into the editor once,
//  track whether it is dirty, validate it, save, discard, publish. The context
//  file next door only hands this value down the tree.
// ════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Bundle } from "@otto/lehrgang";
import { validate, type Problem } from "../../features/validation/lib/validation.js";
import {
  bundleOf,
  useCreateDraft,
  useSaveDraft,
  useDiscardDraft,
  useVersions,
  type Version,
} from "../../features/versions/lib/data.js";
import { usePublish } from "../../features/versions/lib/publish.js";
import { nextVersionNumber, splitVersions, stageKeysOf } from "../lib/draftState.js";
import { emptyBundle } from "../../features/curricula/lib/newCurriculum.js";
import { useOtherCurriculumKeys } from "../../features/curricula/lib/data.js";
import { useStageKeysInUse } from "./useStageKeysInUse.js";
import { useSession } from "@otto/plattform";

/** Stabile leere Menge: eine neue je Render würde `problems` jedes Mal neu bilden. */
const EMPTY: ReadonlySet<string> = new Set();

export type DraftState = {
  readonly loading: boolean;
  readonly error: boolean;
  /** All versions, newest first. */
  readonly versions: readonly Version[];
  readonly published: readonly Version[];
  readonly latestPublished: Version | null;
  readonly publishedBundle: Bundle | null;
  /**
   * Stage keys that are already published — those may never be renamed.
   *
   * Also what `newStageKey` checks against: a key that was handed out once is
   * spent, even if the stage behind it was removed again.
   */
  readonly publishedStageKeys: ReadonlySet<string>;
  /**
   * Published keys that a child has actually worked on — those may not be
   * removed either.
   *
   * A subset of `publishedStageKeys`, and the distinction is the point: what no
   * one ever practised carries no stars and may go. The rule itself is a trigger
   * in the database; this is only what the screen needs to know.
   */
  readonly lockedStageKeys: ReadonlySet<string>;

  /** The open draft, if there is one. */
  readonly draft: Version | null;
  /**
   * What the screen shows: the draft while one is open, otherwise the latest
   * published version.
   *
   * There used to be no third case — without a draft the editor showed "no
   * draft open" and nothing else. But the curriculum exists either way, and
   * looking something up is the most frequent reason to open this app at all.
   * So the pages always have content; `readOnly` says whether it may be typed
   * into.
   */
  readonly bundle: Bundle | null;
  /** True while no draft is open — every field is then locked. */
  readonly readOnly: boolean;
  readonly notes: string;
  readonly dirty: boolean;
  readonly problems: readonly Problem[];

  readonly setBundle: (bundle: Bundle) => void;
  readonly setNotes: (notes: string) => void;
  readonly create: () => void;
  readonly save: () => void;
  readonly discard: () => void;
  readonly publish: () => void;

  readonly creating: boolean;
  readonly saving: boolean;
  readonly publishing: boolean;
  readonly publishError: string | null;
  /**
   * Die belegten Schlüssel noch einmal holen.
   *
   * Zwischen „niemand hat geübt“ und dem Veröffentlichen liegen Tage. Einmal
   * vor der Rückfrage nachgesehen, wird aus dem rohen Fehler der Datenbank in
   * fast allen Fällen der geordnete Weg: der Hinweis kommt zurück, `publishGate`
   * schaltet ab, und daneben steht ein Knopf zum Zurückholen.
   */
  readonly recheck: () => void;
};

/** Was der Entwurfszustand vom Lehrgang wissen muss. Mehr braucht er nicht. */
export type DraftCurriculum = {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
};

/**
 * Der Lehrgang steht in der Adresse; die Kennung kommt mit, weil ein Lehrgang
 * ohne jede Fassung sie sonst nirgends hernehmen könnte. Genau das war der Fall,
 * solange „der Lehrgang“ eine Konstante war: die Kennung wurde aus der ersten
 * gefundenen Fassung gelesen.
 */
export function useDraftState(curriculum: DraftCurriculum | null): DraftState {
  const { client } = useSession();
  const versions = useVersions(client, curriculum?.slug ?? "");
  const foreignKeys = useOtherCurriculumKeys(client, curriculum?.slug ?? "");
  const createMutation = useCreateDraft(client);
  const saveMutation = useSaveDraft(client);
  const discardMutation = useDiscardDraft(client);
  const publishMutation = usePublish(client);

  const [bundle, setBundleState] = useState<Bundle | null>(null);
  const [notes, setNotesState] = useState("");
  const [dirty, setDirty] = useState(false);

  // useMemo so the list keeps its identity when the result is unchanged -
  // otherwise it is a new array on every render and so is every callback
  // depending on it.
  const all = useMemo(() => versions.data ?? [], [versions.data]);
  const { draft, published, latestPublished } = useMemo(() => splitVersions(all), [all]);

  // Copy the draft into the editor once. After that this state is the truth -
  // otherwise every refetch would overwrite what was just typed.
  useEffect(() => {
    if (!draft) {
      setBundleState(null);
      setDirty(false);
      return;
    }
    if (bundle !== null) return;
    const checked = bundleOf(draft);
    if ("bundle" in checked) {
      setBundleState(checked.bundle);
      setNotesState(draft.notes ?? "");
      setDirty(false);
    }
  }, [draft, bundle]);

  const publishedBundle = useMemo(() => {
    if (!latestPublished) return null;
    const checked = bundleOf(latestPublished);
    return "bundle" in checked ? checked.bundle : null;
  }, [latestPublished]);

  const publishedStageKeys = useMemo(
    () => stageKeysOf(publishedBundle),
    [publishedBundle],
  );

  // Der Testschlüssel zählt mit: an ihm hängen die beiden Messungen eines
  // Kindes. Er ist keine Stufe – deshalb steht er nicht in `stageKeysOf` –,
  // aber gegen Entfernen genauso geschützt.
  const askedKeys = useMemo(() => {
    const keys = new Set(publishedStageKeys);
    if (publishedBundle?.assessment) keys.add(publishedBundle.assessment.key);
    return keys;
  }, [publishedStageKeys, publishedBundle]);

  const inUse = useStageKeysInUse(client, askedKeys);
  const { refetch: refetchInUse } = inUse;
  const recheck = useCallback(() => void refetchInUse(), [refetchInUse]);

  const lockedStageKeys = useMemo(
    () => new Set([...askedKeys].filter((key) => inUse.data?.has(key))),
    [askedKeys, inUse.data],
  );

  // Nur der Entwurf wird geprüft. Eine veröffentlichte Fassung hat die Prüfung
  // schon bestanden, und an ihr ließe sich ohnehin nichts beheben - eine Zahl
  // neben einem gesperrten Feld wäre eine Aufgabe ohne Knopf.
  const problems = useMemo(
    () =>
      draft && bundle
        ? validate(bundle, publishedBundle, foreignKeys.data ?? EMPTY, lockedStageKeys)
        : [],
    [draft, bundle, publishedBundle, foreignKeys.data, lockedStageKeys],
  );

  const setBundle = useCallback((next: Bundle) => {
    setBundleState(next);
    setDirty(true);
  }, []);

  const setNotes = useCallback((next: string) => {
    setNotesState(next);
    setDirty(true);
  }, []);

  const forget = useCallback(() => {
    setBundleState(null);
    setNotesState("");
    setDirty(false);
  }, []);

  const create = useCallback(() => {
    if (!curriculum) return;
    createMutation.mutate({
      curriculumId: curriculum.id,
      // Ohne veröffentlichte Fassung beginnt der Entwurf leer – mit Tastatur,
      // Fingern und Sternschwellen, aber ohne Lektion. Vorher stand hier „das
      // Otto-Bundle“, und ein zweiter Lehrgang hätte damit als Kopie des ersten
      // begonnen, inklusive seiner Stufenschlüssel.
      template: publishedBundle ?? emptyBundle(curriculum.slug, curriculum.title),
      nextVersion: nextVersionNumber(all),
    });
  }, [createMutation, all, curriculum, publishedBundle]);

  const save = useCallback(() => {
    if (!draft || !bundle) return;
    saveMutation.mutate(
      { id: draft.id, bundle, notes },
      { onSuccess: () => setDirty(false) },
    );
  }, [draft, bundle, notes, saveMutation]);

  const discard = useCallback(() => {
    if (!draft) return;
    discardMutation.mutate(draft.id, { onSuccess: forget });
  }, [draft, discardMutation, forget]);

  const publish = useCallback(() => {
    if (!draft || !bundle) return;
    void client.auth.getUser().then(({ data }) => {
      if (!data.user) return;
      publishMutation.mutate(
        { id: draft.id, version: draft.version, bundle, notes, userId: data.user.id },
        { onSuccess: forget },
      );
    });
  }, [draft, bundle, notes, client, publishMutation, forget]);

  return {
    loading: curriculum === null || versions.isPending,
    error: versions.isError,
    versions: all,
    published,
    latestPublished,
    publishedBundle,
    publishedStageKeys,
    lockedStageKeys,
    draft,
    // Ohne Entwurf zeigt die Oberfläche die letzte veröffentlichte Fassung.
    bundle: draft ? bundle : publishedBundle,
    readOnly: draft === null,
    notes,
    dirty,
    problems,
    setBundle,
    setNotes,
    create,
    save,
    discard,
    publish,
    creating: createMutation.isPending,
    saving: saveMutation.isPending,
    publishing: publishMutation.isPending,
    publishError:
      publishMutation.error instanceof Error ? publishMutation.error.message : null,
    recheck,
  };
}
